<template>
  <div
    class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden tw:border-0 tw:border-t-[1px] tw:border-solid tw:border-gray-200"
  >
    <SocialEngageAccountLimitAlert
      v-if="integrations.length && !isCisionListenInstagram"
      class="tw:m-4"
    />
    <div
      v-if="integrations.length"
      class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden"
    >
      <div class="tw:px-4 tw:pt-2 tw:font-bold tw:text-denim-900">
        {{ actionTitle }}
      </div>
      <div class="tw:px-4 tw:pt-2">
        {{ actionDescription }}
      </div>
      <div class="tw:flex tw:flex-row tw:px-8 tw:pt-2">
        <SelectAllCheckboxButton
          :disabled="isSelectAllDisabled"
          :indeterminate="selectAllIndetermediate"
          :model-value="selectAll"
          :selected="pagination.pageSize"
          @update:model-value="updateSelectAll"
        />
      </div>
      <div class="tw:flex tw:grow tw:flex-col tw:gap-4 tw:overflow-auto tw:p-4">
        <SocialIntegrationRow
          v-for="(integration, key) in currentIntegrationPage"
          :key="key"
          :integration="integration"
          :is-checked="isIntegrationSelected(integration)"
          :is-disabled="isDisabled(integration)"
          :show-as-expired="props.listType === 'existing'"
          show-checkbox
          @checkbox-clicked="updatedSelectedIntegration"
        />
      </div>
      <div
        class="tw:align-center tw:flex tw:flex-row tw:justify-end tw:border-0 tw:border-t tw:border-solid tw:border-gray-200 tw:p-4"
      >
        <Pagination
          :page-size-options="PAGINATION_PAGE_SIZE_OPTIONS"
          :pagination="pagination"
          @pagination-changed="onPaginationChange"
        />
      </div>
    </div>
    <div
      v-else
      class="tw:col tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center"
    >
      <template
        v-for="(message, index) in accountWarnings"
        :key="index"
      >
        <p
          class="tw:text-denim-900"
          :class="{
            'tw:mb-1 tw:text-base tw:font-bold': index === 0,
            'tw:text-sm': index !== 0,
          }"
        >
          {{ message }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { type Ref, computed, onMounted, ref } from "vue";

import { socialFeatures } from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import SelectAllCheckboxButton from "shared/components/integrations/SelectAllCheckboxButton.vue";
import SocialEngageAccountLimitAlert from "shared/components/integrations/SocialEngageAccountLimitAlert.vue";
import SocialIntegrationRow from "shared/components/integrations/SocialIntegrationRow.vue";
import { Pagination } from "shared/components/Pagination";
import useFeatureWithCredits from "shared/composables/useFeatureWithCredits";
import {
  type SocialIntegration,
  PLATFORM_LABELS,
} from "shared/constants/social";
import type { AppConfig } from "shared/services/appConfig/types";
import useGlobalStore from "shared/stores/global";

interface SocialIntegrationRefreshIntegrationsProps {
  integrations: SocialIntegration[];
  integrationType: keyof typeof PLATFORM_LABELS;
  listType: "existing" | "new";
  identifier: "id" | "remoteId";
}

interface PaginationOptions {
  page: number;
  pageSize: number;
  pageCount: number;
}

const LOCALE = "social_integration_refresh_integration_list";
const PAGE_SIZE = 20;
const PAGINATION_PAGE_SIZE_OPTIONS = [20, 40, 60];

const globalStore = useGlobalStore();

const { remainingQuota, isUnlimitedEngageCredits } = useFeatureWithCredits();

const { appConfig } = storeToRefs(globalStore) as unknown as {
  appConfig: Ref<AppConfig>;
};

const props = defineProps<SocialIntegrationRefreshIntegrationsProps>();

const selectedIntegrations = defineModel<SocialIntegration[]>(
  "selectedIntegrations",
  { default: [] }
);

const currentIntegrationPage = ref([]) as Ref<SocialIntegration[]>;

const pagination = ref<PaginationOptions>({
  page: 1,
  pageCount: 0,
  pageSize: PAGE_SIZE,
});

const actionTitle = computed<string>(() =>
  props.listType === "existing"
    ? getLocaleText(`${LOCALE}.connected_accounts_title`, {
        type: PLATFORM_LABELS[props.integrationType],
      })
    : getLocaleText(`${LOCALE}.accounts_found_title`, {
        type: PLATFORM_LABELS[props.integrationType],
      })
);

const actionDescription = computed<string>(() =>
  props.listType === "existing"
    ? getLocaleText(`${LOCALE}.connected_accounts_description`)
    : getLocaleText(`${LOCALE}.accounts_found_description`, {
        platformName: appConfig.value.platformName,
      })
);

const selectedDifference = computed<SocialIntegration[]>(() => {
  const selectedIntegrationIds = selectedIntegrations.value.map(
    (integration) => integration[props.identifier]
  );

  return currentIntegrationPage.value.filter(
    (pageIntegration) =>
      !selectedIntegrationIds.includes(pageIntegration[props.identifier])
  );
});

const selectAll = computed<boolean>(() => {
  if (!currentIntegrationPage.value.length) return false;

  return !selectedDifference.value.length;
});

const selectAllIndetermediate = computed<boolean>(() =>
  Boolean(
    selectedDifference.value.length &&
      selectedDifference.value.length !== currentIntegrationPage.value.length
  )
);

const accountWarnings = computed<string[]>(() => {
  const type = props.listType;

  return [
    getLocaleText(`${LOCALE}.no_${type}_accounts`, {
      type: PLATFORM_LABELS[props.integrationType],
    }),
    type === "existing"
      ? getLocaleText(`${LOCALE}.no_existing_accounts_description`)
      : "",
  ].filter(Boolean);
});

const isCisionOne = computed(
  () => (appConfig.value as AppConfig).platformName === "CisionOne"
);

const isCisionListenInstagram = computed<boolean>(
  () =>
    isCisionOne.value &&
    props.integrationType === "InstagramSocialIntegration" &&
    socialFeatures.canViewSocialListen()
);

const isSelectAllDisabled = computed<boolean>(
  () =>
    !isCisionListenInstagram.value &&
    props.listType === "new" &&
    !isUnlimitedEngageCredits.value &&
    selectedIntegrations.value.length + currentIntegrationPage.value.length >
      remainingQuota.value
);

onMounted(() => {
  setupPaginatedIntegrations();
});

function isDisabled(integration: SocialIntegration): boolean {
  if (isUnlimitedEngageCredits.value || isCisionListenInstagram.value) {
    return false;
  }

  const existingRecord = selectedIntegrations.value.filter(
    (selectedIntegration) =>
      selectedIntegration[props.identifier] === integration[props.identifier]
  );

  return (
    props.listType === "new" &&
    !existingRecord.length &&
    selectedIntegrations.value.length >= remainingQuota.value
  );
}

function setupPaginatedIntegrations(): void {
  pagination.value.pageCount = Math.ceil(
    props.integrations.length / pagination.value.pageSize
  );

  paginateData();
}

function paginateData(): void {
  const { page, pageSize } = pagination.value;

  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  currentIntegrationPage.value = props.integrations.slice(start, end);
}

function updateSelectAll(): void {
  if (selectAll.value) {
    const integrationsToRemove = currentIntegrationPage.value.flatMap(
      (integration) =>
        isIntegrationSelected(integration)
          ? [integration[props.identifier]]
          : []
    );

    selectedIntegrations.value = selectedIntegrations.value.filter(
      (integration) =>
        !integrationsToRemove.includes(integration[props.identifier])
    );

    return;
  }

  const integrationsToAdd = currentIntegrationPage.value.filter(
    (integration) => !isIntegrationSelected(integration)
  );

  selectedIntegrations.value = [
    ...selectedIntegrations.value,
    ...integrationsToAdd,
  ];
}

function onPaginationChange({ page, pageSize }: PaginationOptions): void {
  pagination.value.page = page;
  pagination.value.pageSize = pageSize;

  pagination.value.pageCount = Math.ceil(props.integrations.length / pageSize);

  paginateData();
}

function isIntegrationSelected(integration: SocialIntegration): boolean {
  return selectedIntegrations.value.some(
    (selectedIntegration) =>
      selectedIntegration[props.identifier] === integration[props.identifier]
  );
}

function updatedSelectedIntegration(integration: SocialIntegration): void {
  if (isIntegrationSelected(integration)) {
    selectedIntegrations.value = selectedIntegrations.value.filter(
      (selectedIntegration) =>
        selectedIntegration[props.identifier] !== integration[props.identifier]
    );
  } else {
    selectedIntegrations.value = [...selectedIntegrations.value, integration];
  }
}

defineExpose({
  selectedIntegrations,
});
</script>
